import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Link2, Unlink, Check, X, Loader2, Building2, Package, Phone, Mail, MapPin, Settings2, IndianRupee } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VendorLinkVendors({ token: propToken, vendorType }) {
  const getHeaders = () => ({ Authorization: `Bearer ${propToken || localStorage.getItem('vendor_token')}` });

  const [tab, setTab] = useState('connected');
  const [search, setSearch] = useState('');
  const [available, setAvailable] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);

  // Product management dialog state
  const [productDialog, setProductDialog] = useState({ open: false, vendor: null, products: [], loading: false });
  const [savingProducts, setSavingProducts] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');

  const fetchAvailable = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/vendor-vendor-mappings/available-vendors?search=${search}`, { headers: getHeaders() });
      setAvailable(res.data);
    } catch (err) { console.error('Failed to fetch vendors:', err); }
    setLoading(false);
  }, [search]);

  const fetchIncoming = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/vendor-vendor-mappings/incoming-requests`, { headers: getHeaders() });
      setIncoming(res.data);
    } catch (err) { console.error('Failed to fetch incoming:', err); }
  }, []);

  const fetchConnections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/vendor-vendor-mappings/my-connections`, { headers: getHeaders() });
      setConnections(res.data);
    } catch (err) { console.error('Failed to fetch connections:', err); }
  }, []);

  useEffect(() => { fetchAvailable(); fetchIncoming(); fetchConnections(); }, [fetchAvailable, fetchIncoming, fetchConnections]);

  const sendRequest = async (targetId) => {
    try {
      await axios.post(`${API}/api/vendor-vendor-mappings/request-connection`, { target_vendor_id: targetId }, { headers: getHeaders() });
      toast.success('Request sent');
      fetchAvailable();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const respond = async (mappingId, action) => {
    try {
      await axios.put(`${API}/api/vendor-vendor-mappings/${action}/${mappingId}`, {}, { headers: getHeaders() });
      toast.success(action === 'accept' ? 'Approved' : 'Rejected');
      fetchIncoming(); fetchConnections();
    } catch { toast.error('Failed'); }
  };

  const unlink = async (mappingId) => {
    if (!window.confirm('Remove this connection?')) return;
    try {
      await axios.delete(`${API}/api/vendor-vendor-mappings/unlink/${mappingId}`, { headers: getHeaders() });
      toast.success('Removed');
      fetchConnections(); fetchAvailable();
    } catch { toast.error('Failed'); }
  };

  // Product management
  const openManageProducts = async (vendor) => {
    setProductDialog({ open: true, vendor, products: [], loading: true });
    try {
      const res = await axios.get(`${API}/api/vendor-vendor-mappings/vendor/${vendor.vendor_id}/products`, { headers: getHeaders() });
      setProductDialog(prev => ({ ...prev, products: res.data.products || [], loading: false }));
    } catch {
      toast.error('Failed to load products');
      setProductDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const toggleProductVisibility = (productId) => {
    setProductDialog(prev => ({
      ...prev,
      products: prev.products.map(p => p.product_id === productId ? { ...p, visible: !p.visible } : p)
    }));
  };

  const updateCustomPrice = (productId, price) => {
    setProductDialog(prev => ({
      ...prev,
      products: prev.products.map(p => p.product_id === productId ? { ...p, custom_price: price === '' ? null : parseFloat(price) } : p)
    }));
  };

  const saveProductSettings = async () => {
    setSavingProducts(true);
    try {
      const visibility = {};
      const custom_prices = {};
      productDialog.products.forEach(p => {
        visibility[p.product_id] = p.visible;
        if (p.custom_price != null && p.custom_price !== '' && !isNaN(p.custom_price) && p.custom_price > 0) {
          custom_prices[p.product_id] = parseFloat(p.custom_price);
        }
      });
      await axios.put(`${API}/api/vendor-vendor-mappings/vendor/${productDialog.vendor.vendor_id}/products`,
        { product_visibility: visibility, custom_prices }, { headers: getHeaders() }
      );
      toast.success('Product settings updated');
      setProductDialog({ open: false, vendor: null, products: [], loading: false });
    } catch { toast.error('Failed to save'); }
    setSavingProducts(false);
  };

  const targetLabel = vendorType === 'chemist' ? 'Pharma Companies' : vendorType === 'pharma' ? 'Chemists' : 'Vendors';

  return (
    <div className="space-y-4" data-testid="vendor-link-vendors">
      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'incoming') fetchIncoming(); if (v === 'connected') fetchConnections(); }}>
        <TabsList>
          <TabsTrigger value="connected" data-testid="connected-vendors-tab">My {targetLabel} ({connections.length})</TabsTrigger>
          <TabsTrigger value="search" data-testid="search-vendors-tab">Find {targetLabel}</TabsTrigger>
          <TabsTrigger value="incoming" data-testid="incoming-requests-tab">Incoming ({incoming.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="connected">
          {connections.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Building2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>No connections yet. Search and connect with {targetLabel.toLowerCase()}.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {connections.map(c => (
                <Card key={c.mapping_id} className="hover:shadow-sm transition" data-testid={`connected-vendor-${c.vendor_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Building2 className="w-5 h-5 text-teal-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm">{c.vendor_name}</p>
                          <Badge variant="secondary" className="text-[10px] capitalize mt-0.5">{c.vendor_type}</Badge>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-slate-500">
                            {c.vendor_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.vendor_phone}</span>}
                            {c.vendor_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.vendor_email}</span>}
                            {c.vendor_address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.vendor_address}</span>}
                            {c.gst_number && <span className="flex items-center gap-1 font-mono">GST: {c.gst_number}</span>}
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">Connected since {new Date(c.connected_since).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openManageProducts(c)} data-testid={`manage-products-${c.vendor_id}`}>
                          <Settings2 className="w-3 h-3 mr-1" /> Manage Products
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-500 border-red-200 h-8 text-xs" onClick={() => unlink(c.mapping_id)}>
                          <Unlink className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="search">
          <div className="flex gap-2 mb-4">
            <Input placeholder={`Search ${targetLabel.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchAvailable()} data-testid="vendor-search-input" />
            <Button size="sm" onClick={fetchAvailable}><Search className="w-4 h-4" /></Button>
          </div>
          {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
            <div className="space-y-2">
              {available.length === 0 ? <p className="text-center text-slate-400 py-6">No vendors found</p> :
                available.map(v => (
                  <div key={v.id} className="p-3 border rounded-lg flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-8 h-8 text-slate-400" />
                      <div>
                        <p className="font-medium text-sm">{v.name}</p>
                        <p className="text-xs text-slate-500">{v.email} <Badge variant="secondary" className="text-[10px] capitalize ml-1">{v.vendor_type}</Badge></p>
                      </div>
                    </div>
                    {v.connection_status === 'approved' ? (
                      <Badge className="bg-green-100 text-green-700">Connected</Badge>
                    ) : v.connection_status === 'pending' ? (
                      <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                    ) : (
                      <Button size="sm" onClick={() => sendRequest(v.id)}><Link2 className="w-4 h-4 mr-1" /> Connect</Button>
                    )}
                  </div>
                ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="incoming">
          {incoming.length === 0 ? <p className="text-center text-slate-400 py-6">No pending requests</p> :
            incoming.map(req => (
              <div key={req.id} className="p-3 border rounded-lg flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-sm">{req.vendor_a_name || req.vendor_b_name}</p>
                  <p className="text-xs text-slate-500 capitalize">{req.vendor_a_type || req.vendor_b_type}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 text-xs" onClick={() => respond(req.id, 'accept')}>
                    <Check className="w-3 h-3 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => respond(req.id, 'reject')}>
                    <X className="w-3 h-3 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
        </TabsContent>
      </Tabs>

      {/* Manage Products Dialog */}
      <Dialog open={productDialog.open} onOpenChange={(o) => { if (!o) { setProductDialog({ open: false, vendor: null, products: [], loading: false }); setProductSearchQuery(''); } }}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-teal-600" />
              Manage Products - {productDialog.vendor?.vendor_name}
            </DialogTitle>
          </DialogHeader>
          {productDialog.loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Toggle visibility and set custom B2B prices for this {vendorType === 'pharma' ? 'chemist' : 'vendor'}.</p>
              {productDialog.products.length > 0 && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search products..."
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    className="pl-9 h-8 text-sm"
                    data-testid="vendor-product-search-input"
                  />
                </div>
              )}
              {productDialog.products.length === 0 ? (
                <p className="text-center py-6 text-slate-400">No products to manage</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {productDialog.products
                    .filter(p => {
                      if (!productSearchQuery) return true;
                      const q = productSearchQuery.toLowerCase();
                      return (p.product_name || '').toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q) || (p.generic_name || '').toLowerCase().includes(q);
                    })
                    .map(p => (
                    <div key={p.product_id} className={`flex items-center justify-between p-2 border rounded-lg text-sm ${!p.visible ? 'opacity-50 bg-slate-50' : ''}`} data-testid={`product-manage-${p.product_id}`}>
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Switch checked={p.visible} onCheckedChange={() => toggleProductVisibility(p.product_id)} data-testid={`toggle-${p.product_id}`} />
                        <div className="min-w-0">
                          <p className="font-medium text-xs truncate">{p.product_name}</p>
                          <p className="text-[10px] text-slate-400">{p.category} | MRP ₹{p.mrp} | B2B ₹{p.b2b_price}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <IndianRupee className="w-3 h-3 text-slate-400" />
                        <Input
                          type="number" min="0" step="0.5"
                          placeholder="Custom"
                          value={p.custom_price ?? ''}
                          onChange={(e) => updateCustomPrice(p.product_id, e.target.value)}
                          className="w-20 h-7 text-xs"
                          data-testid={`custom-price-${p.product_id}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between border-t pt-3">
                <p className="text-xs text-slate-500">
                  {productDialog.products.filter(p => p.visible).length}/{productDialog.products.length} visible
                  {productDialog.products.filter(p => p.custom_price != null && p.custom_price !== '' && !isNaN(p.custom_price) && p.custom_price > 0).length > 0 &&
                    ` | ${productDialog.products.filter(p => p.custom_price != null && p.custom_price !== '' && !isNaN(p.custom_price) && p.custom_price > 0).length} custom prices`
                  }
                </p>
                <Button onClick={saveProductSettings} disabled={savingProducts} data-testid="save-product-settings-btn">
                  {savingProducts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Settings
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
